import React, { useEffect, useState, useContext } from 'react'
import styles from './FiltersList.module.scss'
import Spacer from '../Spacer/Spacer'
import { ReactComponent as CloseIcon } from '../../assets/media/icons/close-icon.svg'
import { ReactComponent as FiltersIcon } from '../../assets/media/icons/filter-funnel.svg'
import FiltersCategory from './FiltersCategory/FiltersCategory'
import PropTypes from 'prop-types'
import ApplicationContext from '../../context/ApplicationContext'
import { useSearchLocation, useFocusTrap } from '../../hooks'
import FiltersRadio from './FiltersRadio/FiltersRadio'

const FiltersList = ({ openFiltersList, toggleFiltersList, filters, appliedFilters, onSearch }) => {
    const [applicationData] = useContext(ApplicationContext)
    const [localizations] = useState(applicationData.localizations)
    const [localSelectedFilters, setLocalSelectedFilters] = useState(appliedFilters)
    const { hideSort, sortOptions, handleSortChange, sortValue } = useSearchLocation()
    const dialogRef = useFocusTrap(openFiltersList)

    useEffect(() => {
        setLocalSelectedFilters(appliedFilters)
    }, [appliedFilters])

    // Handle Escape key to close modal
    useEffect(() => {
        const handleEscape = e => {
            if (e.key === 'Escape' && openFiltersList) {
                toggleFiltersList()
            }
        }

        document.addEventListener('keydown', handleEscape)
        return () => document.removeEventListener('keydown', handleEscape)
    }, [openFiltersList, toggleFiltersList])

    const handleFilterClick = filter => {
        setLocalSelectedFilters(prevState => {
            return prevState?.some(currentFilter => currentFilter.value === filter.value)
                ? prevState?.filter(currentFilter => currentFilter.value !== filter.value)
                : prevState?.concat(filter) || [filter]
        })
    }

    return (
        <div className={`${styles.filtersListWrapper} ${openFiltersList ? styles.open : ''}`}>
            <div
                ref={dialogRef}
                className={styles.container}
                role='dialog'
                aria-label={`${localizations.filters}${!hideSort ? ` / ${localizations.sort}` : ''}`}
                aria-modal='true'
            >
                {openFiltersList && (
                    <>
                        <div className={styles.topBar}>
                            <div className={styles.topBar_heading}>
                                <FiltersIcon />
                                <span>
                                    {localizations.filters}
                                    {!hideSort && ` / ${localizations.sort}`}
                                </span>
                            </div>

                            <button type='button' className={styles.topBar_closeModal} onClick={toggleFiltersList} aria-label='Close filters'>
                                <CloseIcon />
                            </button>
                        </div>

                        <div className={styles.content}>
                            {!hideSort && (
                                <>
                                    <FiltersRadio
                                        title={localizations.sortBy}
                                        selectedFilter={sortValue}
                                        onFilterSelect={handleSortChange}
                                        filters={sortOptions}
                                    />
                                    <Spacer />
                                </>
                            )}

                            <h6 className={styles.content_heading}>{localizations.filterBy}</h6>

                            {filters?.map((filter, index) => {
                                return (
                                    <FiltersCategory
                                        selectedFilters={localSelectedFilters}
                                        onFilterSelect={handleFilterClick}
                                        key={index}
                                        category={filter.category}
                                        filters={filter.filters}
                                        preOpen={index === 0}
                                    />
                                )
                            })}
                        </div>

                        <Spacer border={true} />

                        <div className={styles.bottomBar}>
                            <button type='button' className={styles.bottomBar_clearFilters} onClick={() => setLocalSelectedFilters([])}>
                                {localizations.clear} {localSelectedFilters?.length > 0 && `(${localSelectedFilters?.length})`}
                            </button>

                            <button type='button' className={styles.bottomBar_showResults} onClick={() => onSearch(localSelectedFilters)}>
                                {localizations.apply}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

FiltersList.propTypes = {
    openFiltersList: PropTypes.bool,
    toggleFiltersList: PropTypes.func,
    onSearch: PropTypes.func,
    filters: PropTypes.array,
    appliedFilters: PropTypes.array,
    onClear: PropTypes.func,
}

export default FiltersList
